// The order builder's basket (Oct 2026). Every way into ordering opens one builder: what's
// below par, a bundle, or nothing. You shape the whole order there, across suppliers, before
// anything is drafted; then one draft per supplier. The basket is only stock ids and
// quantities: who it's bought from, the price and the pack are read off the stock item each
// time, so a change made in the builder (stop buying it from them) shows straight away.

// Quantities are in stock units, in whole packs where the supplier sells in packs.
export const packOf = (item) => Math.max(Number(item?.pack_size) || 1, 0.001);
const round3 = (n) => Math.round(n * 1000) / 1000;

export function toPack(needed, pack) {
  const p = Number(pack) || 1;
  if (needed <= 0) return 0;
  if (p <= 1) return round3(needed);
  return round3(Math.ceil(round3(needed / p)) * p);
}

// What to order of an item when it's first added: up to par if it's below, counting what's
// already on order, else one pack.
export function firstQuantity(item, onOrder = 0) {
  const par = Number(item.par_level) || 0;
  const have = Number(item.quantity) || 0;
  const coming = Number(onOrder) || 0;
  return par > have + coming ? toPack(par - have - coming, item.pack_size) : packOf(item);
}

export const emptyBasket = (from = 'empty') => ({ from, lines: [] });

// What's below par (`purchase-orders/suggestions`), as a basket. No-supplier items stay out:
// there's nobody to order them from. What's below par but on order in full (`covered`) comes
// in at 0, so it's seen, said to be on its way, and can be added to.
export function fromSuggestions(groups) {
  return {
    from: 'par',
    lines: (groups || []).filter((g) => g.supplier_id).flatMap((g) => [
      ...g.lines.map((l) => ({ stock_id: l.stock_id, quantity: l.quantity })),
      ...(g.covered || []).map((l) => ({ stock_id: l.stock_id, quantity: 0 })),
    ]),
  };
}

// A bundle's lines, on this site's stock: by id, or by name where it was saved at another site.
export function fromBundle(bundle, stock) {
  const byName = Object.fromEntries((stock || []).map((s) => [String(s.name).trim().toLowerCase(), s]));
  const ids = new Set((stock || []).map((s) => s.id));
  return {
    from: 'bundle',
    lines: (bundle?.lines || []).map((l) => {
      const id = ids.has(l.stock_id) ? l.stock_id : byName[String(l.name || '').trim().toLowerCase()]?.id;
      return id ? { stock_id: id, quantity: Number(l.quantity) || 0 } : null;
    }).filter(Boolean),
  };
}

export function add(basket, item, quantity = firstQuantity(item)) {
  const at = basket.lines.find((l) => l.stock_id === item.id);
  if (at) return setQuantity(basket, item.id, at.quantity + packOf(item));
  return { ...basket, lines: [...basket.lines, { stock_id: item.id, quantity }] };
}

export function setQuantity(basket, stockId, quantity) {
  return { ...basket, lines: basket.lines.map((l) => (l.stock_id === stockId ? { ...l, quantity: Math.max(0, round3(quantity)) } : l)) };
}

// One pack more or less. Down to nothing takes it off the order (Martin, 27 Oct): it's quick
// to add back from search or "+ Add from". A line already at 0 (on order in full, shown for
// what's coming) comes off with − too, and + starts it at a pack.
export function step(basket, item, dir) {
  const at = basket.lines.find((l) => l.stock_id === item.id);
  if (!at) return basket;
  const next = round3(at.quantity + dir * packOf(item));
  return next > 0 ? setQuantity(basket, item.id, next) : remove(basket, item.id);
}

export const remove = (basket, stockId) => ({ ...basket, lines: basket.lines.filter((l) => l.stock_id !== stockId) });

// The basket by supplier, for the page: each group's lines with the item and the cost, the
// group's total, and any open draft that supplier already has. Biggest first. Lines whose item
// has gone, or has no supplier now, are under `orphans`.
export function grouped(basket, stockById, suppliersById, openDrafts = []) {
  const groups = {};
  const orphans = [];
  for (const l of basket.lines) {
    const item = stockById[l.stock_id];
    if (!item || !item.supplier_id || !suppliersById[item.supplier_id]) { orphans.push({ ...l, item }); continue; }
    const g = groups[item.supplier_id] || (groups[item.supplier_id] = {
      supplier_id: item.supplier_id, supplier_name: suppliersById[item.supplier_id].name, lines: [], total: 0,
      drafts: openDrafts.filter((d) => d.supplier_id === item.supplier_id && d.status === 'draft'),
    });
    const cost = round2(l.quantity * (Number(item.cost_per_unit) || 0));
    g.lines.push({ ...l, item, cost });
    g.total = round2(g.total + cost);
  }
  // A supplier with every line at 0 gets no draft this time.
  Object.values(groups).forEach((g) => { g.ordering = g.lines.some((l) => l.quantity > 0); });
  const list = Object.values(groups).sort((a, b) => b.total - a.total);
  return { groups: list, orphans, total: round2(list.reduce((t, g) => t + g.total, 0)) };
}

const round2 = (n) => Math.round(n * 100) / 100;

// What Create drafts sends: each group, added to the open draft chosen for it (`choices`:
// supplier id → draft id, or 'new').
export function draftBody(groups, choices = {}) {
  return {
    groups: groups.filter((g) => g.lines.some((l) => l.quantity > 0)).map((g) => ({
      supplier_id: g.supplier_id,
      lines: g.lines.filter((l) => l.quantity > 0).map((l) => ({ stock_id: l.stock_id, quantity: l.quantity })),
      ...(choices[g.supplier_id] && choices[g.supplier_id] !== 'new' ? { add_to: choices[g.supplier_id] } : {}),
    })),
  };
}

// "1 of 2 kegs" against par; "0" with no par set.
export function againstPar(item) {
  return { have: Number(item.quantity) || 0, par: Number(item.par_level) || 0, low: Number(item.min_quantity) > 0 && Number(item.quantity) <= Number(item.min_quantity) };
}

// Search: the site's products from any supplier, and suppliers, by name.
export function search(term, stock, suppliers) {
  const t = String(term || '').trim().toLowerCase();
  if (!t) return { products: [], suppliers: [] };
  return {
    products: (stock || []).filter((s) => s.supplier_id && String(s.name).toLowerCase().includes(t)).slice(0, 8),
    suppliers: (suppliers || []).filter((s) => String(s.name).toLowerCase().includes(t)).slice(0, 4),
  };
}
